import React, { useState, useEffect, useRef } from "react";

import { Button, Spin, Input, MultiSelect, Modal, Message } from "../../../BP2Components";
import { getMessageStep } from "../../../../services/api/Api";
import {
   sendEmailMessage,
   addComment,
   getUserMailContacts,
   getAttachments,
   getMessageActions
} from "../../../../services/restAPI/RestApi";
import { useDispatch, useSelector } from "react-redux";
import { setComments, setMessageHistory } from "../../../../appRedux/actions/FormsV2";
import { useIntl } from "react-intl";
import { Select } from "antd";
import IntlMessages from "../../../../util/IntlMessages";
import Collapse from "../../../BP2Components/Collapse";

import "./MailModal.less";
import {
   Icon_Resize,
   Icon_Download,
   Icon_Trash,
   Icon_Clip,
   Icon_Sent
} from "../../../BP2Components/Icons/icons";

const { Option } = Select;

// buttons are rendered it two files: MessageButtons.jsx and AddComments.js
// We can't use mailContent as a state, becasue it's resets mouse focus every time it's changed

export const downloadAttachment = (file, blob) => {
   if (!file) return;
   const link = document.createElement("a");
   const url = URL.createObjectURL(blob ?? file);
   link.href = url;
   link.setAttribute("download", file.name);
   document.body.appendChild(link);
   link.click();

   setTimeout(() => {
      URL.revokeObjectURL(url);
      link.parentNode?.removeChild(link);
   }, 100);
};

const MailModal = ({
   instanceId = null,
   recordId = null,
   children,
   className = "",
   btnClassName,
   alwaysVisible,
   sendMailType = "send",
   mailContent,
   previousMail = {},
   buttonType = "primary",
   previousAttachments = null
}) => {
   const [isOpen, setIsOpen] = useState(false);
   const mailContentRef = useRef(null);
   const [emailData, setEmailData] = useState({});
   const [subject, setSubject] = useState("");
   const [selectedFiles, setSelectedFiles] = useState([]);
   const [previousFiles, setPreviousFiles] = useState([]);
   const [isResized, setIsResized] = useState(false);
   const [isSending, setIsSending] = useState(false);
   const { mailTitle } = useSelector(({ formsv2 }) => formsv2);
   const { mailBoxes } = useSelector(({ messages }) => messages);
   const [mailBoxId, setMailboxId] = useState(mailBoxes?.length > 0 ? mailBoxes?.[0].id : null);
   const [contactOptions, setContactOptions] = useState([]);
   const dispatch = useDispatch();
   const intl = useIntl();
   const mailSubject = previousMail?.["Temat"] ?? previousMail?.["subject"];
   const fileInputRef = useRef(null);

   const getPreviousAttachment = (file) => {
      getAttachments(mailBoxId, previousMail.Id, file.partId).then((url) => {
         const link = document.createElement("a");
         link.href = url;
         link.setAttribute("download", file.filename);
         document.body.appendChild(link);
         link.click();
         link.parentNode?.removeChild(link);
      });
   };
   const removePreviousAttachment = (file) => {
      setPreviousFiles(previousFiles.filter((prevFile) => prevFile.partId !== file.partId));
   };

   useEffect(() => {
      if (!isOpen) return;
      setPreviousFiles(previousAttachments);
      getUserContacts();

      if (sendMailType === "forward") {
         setSubject((mailSubject.slice(0, 4) !== "Fwd:" ? "Fwd: " : "") + mailSubject);
      }

      if (sendMailType === "reply") {
         const recipients = [previousMail?.["Nadawca"]?.email ?? previousMail["fromAddress"]];
         const ccRecipients = [...previousMail?.["ccAddresses"]];

         setEmailData({ ...emailData, recipients, ccRecipients });
         setSubject((mailSubject.slice(0, 3) !== "Re:" ? "Re: " : "") + mailSubject);
      }

      if (sendMailType === "replyAll") {
         const recipients = [previousMail?.["Nadawca"]?.email ?? previousMail["fromAddress"]];
         const ccRecipients = [...previousMail?.["ccAddresses"]];
         const bccRecipients = [...previousMail?.["bccAddresses"]];

         setEmailData({ ...emailData, recipients, ccRecipients, bccRecipients });
         setSubject((mailSubject.slice(0, 3) !== "Re:" ? "Re: " : "") + mailSubject);
      }
   }, [isOpen]);

   const getUserContacts = async () => {
      try {
         const res = await getUserMailContacts();
         setContactOptions(res?.data?.contactList.map((item) => ({ value: item, label: item })));
      } catch (err) {
         console.log(err);
      }
   };

   const clearModal = () => {
      setSubject("");
      setSelectedFiles([]);
      setIsOpen(false);
   };

   const confirm = (e) => {
      e.preventDefault();
      Message.loading(intl.formatMessage({ id: "global.loading" }));
      const query = new URLSearchParams(window.location.search);
      const actionIdFromUrl = query.get("actionId");
      const { recipients, ccRecipients, bccRecipients } = emailData;
      try {
         if (
            mailBoxId &&
            subject &&
            (recipients?.length > 0 || ccRecipients?.length > 0 || bccRecipients?.length > 0)
         ) {
            // creating new FormData() - function to convert form  to binary code in order to send it together with a file (jpg, pdf, etc.).
            const bodyFormData = new FormData();
            // adding data to the FormData() list
            bodyFormData.append("mailboxId", mailBoxId);
            bodyFormData.append("message", mailContentRef.current.innerHTML);
            bodyFormData.append("subject", subject);
            bodyFormData.append("to", recipients);
            if (instanceId) bodyFormData.append("instanceId", instanceId);
            if (recordId) bodyFormData.append("recordId", recordId);
            // If we send these three parameters empty, the backend will return a 500 status.
            if (ccRecipients?.length > 0) bodyFormData.append("cc", ccRecipients);
            if (bccRecipients?.length > 0) bodyFormData.append("bcc", bccRecipients);
            // attaching more than one file
            if (selectedFiles) {
               selectedFiles.forEach((file) => {
                  bodyFormData.append("attachments", file);
               });
            }
            if (sendMailType !== "send") {
               bodyFormData.append(
                  `${sendMailType === "replyAll" ? "reply" : sendMailType}ToMessageId`,
                  previousMail?.["Id"] ?? previousMail?.["messageId"]
               );
            }
            try {
               // Send an email
               setIsSending(true);
               if (isSending === false) {
                  const filePartIds =
                     Array.isArray(previousFiles) && previousFiles.length > 0
                        ? previousFiles?.map((file) => file.partId)
                        : null;
                  sendEmailMessage(
                     bodyFormData,
                     sendMailType === "replyAll" ? "reply" : sendMailType,
                     filePartIds ? filePartIds : null
                  )
                     .then((res) => {
                        //getting comment and message actions after successful email sending
                        if (actionIdFromUrl && res.status === 200) {
                           getMessageStep(actionIdFromUrl)
                              .then((message) => {
                                 if (message.status === 200) {
                                    dispatch(setComments(message?.data?.step?.comments));
                                    getMessageActions(message.data.step.instanceId).then((res) => {
                                       dispatch(setMessageHistory(res?.data));
                                    });
                                 }
                              })
                              .catch((err) => {
                                 console.log(err);
                              });
                        }
                        clearModal();
                        setIsOpen(false);
                        Message.destroy();
                        Message.success(intl.formatMessage({ id: "global.done" }));
                     })
                     .catch(() => {
                        Message.destroy();
                        Message.error(intl.formatMessage({ id: "global.error" }));
                     })
                     .finally(() => {
                        setIsSending(false);
                     });
               }
            } catch (err) {
               Message.destroy();
               Message.error(intl.formatMessage({ id: "global.error" }));
               console.error(`email cannot be sended, Error: ${err}`);
            }
         } else {
            Message.destroy();
            Message.error(
               intl.formatMessage({ id: "mailbox.subjectAndRecipientsFieldsCannotBeEmpty" })
            );
         }
      } catch (err) {
         console.error(`An error occurred: ${err}`);
      }
   };

   const onMultiSelectChange = (e, selectKey) => {
      const newEmailData = { ...emailData };
      const addressList = Object.keys(e).map((key) => e[key].label);
      newEmailData[selectKey] = addressList;
      setEmailData(newEmailData);
   };
   const onSubjectChange = (e) => {
      setSubject(e.target.value);
   };
   const onSenderChange = (e) => {
      setMailboxId(e);
   };

   const handleFileChange = (event) => {
      event.persist();
      event.target.files[0] &&
         setSelectedFiles((prevFiles) => [...prevFiles, event.target.files[0]]);
   };
   const handleDeleteFile = (file) => {
      fileInputRef.current.value = "";
      setSelectedFiles((prevFiles) => prevFiles.filter((prevFile) => prevFile !== file));
   };

   const formatFileSize = (bytes) => {
      const sufixes = ["B", "kB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sufixes[i]}`;
   };

   return (
      <>
         {isOpen && (
            <Modal
               topBarContent={
                  <>
                     <div className="mail-modal__header">
                        <div className="mail-modal__header__title">
                           <Icon_Sent fill="none" stroke="#1C4387" />
                           <p className="p2">
                              <IntlMessages id="modal.newMessage" />
                           </p>
                        </div>
                     </div>
                     <Button
                        className="mail-modal__resize-btn"
                        onClick={() => setIsResized(!isResized)}
                     >
                        <Icon_Resize fill="#64748B" stroke="none" />
                     </Button>
                  </>
               }
               width={isResized ? "90%" : 640}
               height={isResized ? "90%" : "auto"}
               mainStyles={
                  !isResized
                     ? {
                          position: "fixed",
                          bottom: 0,
                          right: "50px"
                       }
                     : {
                          top: "50%",
                          right: "50%",
                          transform: "translate(50%,-50%)"
                       }
               }
               calculatePosition={false}
               isOpen={isOpen}
               wrapClassName={`mail-modal ${className}`}
               backgroundShadow={isResized}
               onCancel={() => setIsOpen(false)}
               children={
                  mailBoxes.length > 0 ? (
                     mailTitle || alwaysVisible ? (
                        <div className="main-mail-form">
                           <div className="mail-select-wrapper">
                              <p className="p2">
                                 <IntlMessages id="modal.sender" />:
                              </p>
                              <Select
                                 classNamePrefix="mail-sender"
                                 className="mail-sender"
                                 defaultValue={mailBoxes?.length > 0 ? mailBoxes?.[0].username : ""}
                                 onSelect={onSenderChange}
                              >
                                 {mailBoxes?.map((mail) => (
                                    <Option value={mail.id} key={mail.id}>
                                       {mail.username}
                                    </Option>
                                 ))}
                              </Select>
                           </div>
                           <div className="mail-select-wrapper">
                              <p className="p2">
                                 <IntlMessages id="email.writeMailRecipient" />:
                              </p>
                              {/* TODO: We can try to use if statement to prevent rendering MultiSelect before defaultValue would be given */}
                              <MultiSelect
                                 placeholder={intl.formatMessage({
                                    id: "modal.mailAdresses"
                                 })}
                                 className="mail-recipient"
                                 isMulti={true}
                                 isSearchable={true}
                                 hideArrow={true}
                                 onChange={(e) => {
                                    onMultiSelectChange(e, "recipients");
                                 }}
                                 value={emailData?.recipients?.map((option) => ({
                                    value: option,
                                    label: option,
                                    __isNew__: false
                                 }))}
                                 options={contactOptions}
                              />
                           </div>
                           <div className="gx-pb-5 gx-pt-0">
                              <div className="modal-collapse-container">
                                 <Collapse
                                    style={{ overflow: "unset" }}
                                    header={
                                       <span>
                                          <IntlMessages id="modal.mailCcAdress" />
                                       </span>
                                    }
                                    contentClassName="cc-content"
                                    headerClassName="mail-list__item__collapse__header mail-modal-collapse-header cc-header"
                                 >
                                    <div className="mail-modal__select-wrapper">
                                       <p>
                                          <IntlMessages id="modal.mailCcAdress" />:
                                       </p>
                                       <MultiSelect
                                          className="mail-recipient"
                                          isMulti={true}
                                          isSearchable={true}
                                          hideArrow={true}
                                          placeholder={intl.formatMessage({
                                             id: "modal.mailAdresses"
                                          })}
                                          onChange={(e) => {
                                             onMultiSelectChange(e, "ccRecipients");
                                          }}
                                          value={emailData?.ccRecipients?.map((option) => ({
                                             value: option,
                                             label: option,
                                             __isNew__: false
                                          }))}
                                          options={contactOptions}
                                       />
                                    </div>
                                 </Collapse>
                                 <Collapse
                                    style={{ overflow: "unset" }}
                                    header={
                                       <span>
                                          <IntlMessages id="modal.mailBccAdress" />
                                       </span>
                                    }
                                    contentClassName="cc-content"
                                    headerClassName="mail-list__item__collapse__header mail-modal-collapse-header bcc-header"
                                 >
                                    <div className="mail-modal__select-wrapper">
                                       <p>
                                          <IntlMessages id="modal.mailBccAdress" />:
                                       </p>
                                       <MultiSelect
                                          className="mail-recipient"
                                          isMulti={true}
                                          isSearchable={true}
                                          hideArrow={true}
                                          placeholder={intl.formatMessage({
                                             id: "modal.mailAdresses"
                                          })}
                                          onChange={(e) => {
                                             onMultiSelectChange(e, "bccRecipients");
                                          }}
                                          value={emailData?.bccRecipients?.map((option) => ({
                                             value: option,
                                             label: option,
                                             __isNew__: false
                                          }))}
                                          options={contactOptions}
                                       />
                                    </div>
                                 </Collapse>
                              </div>
                           </div>
                           <div className="email-container">
                              <Input
                                 placeholder={intl.formatMessage({
                                    id: "modal.mailSubject"
                                 })}
                                 onChange={onSubjectChange}
                                 className="mail-subject"
                                 value={subject}
                              />
                              {mailContent?.type === "textHtml" ? (
                                 <div
                                    ref={mailContentRef}
                                    contentEditable={true}
                                    className="mail-HTML-content"
                                    placeholder={intl.formatMessage({ id: "modal.messageContent" })}
                                    dangerouslySetInnerHTML={{
                                       __html:
                                          sendMailType !== "send"
                                             ? "<br><br>==============================<br><br>" +
                                               mailContent?.content
                                             : mailContent?.content
                                    }}
                                 />
                              ) : (
                                 <div
                                    placeholder={intl.formatMessage({ id: "modal.messageContent" })}
                                    ref={mailContentRef}
                                    contentEditable={true}
                                    className="mail-HTML-content"
                                    style={{
                                       whiteSpace: "pre-wrap"
                                    }}
                                 >
                                    {sendMailType !== "send"
                                       ? "\n\n==============================\n\n" +
                                         mailContent?.content
                                       : mailContent?.content}
                                 </div>
                              )}
                              <div className="previous-attachment-container">
                                 {previousFiles && (
                                    previousFiles.map((file) => {
                                       return (
                                          <div
                                             className="mail-modal__file-attachment"
                                             key={`previous-${file.partId}`}
                                          >
                                             <div className="mail-modal__file-attachment__title">
                                                <p className="p1">{file?.filename}</p>
                                             </div>
                                             <div className="mail-modal__btns-wrapper">
                                                <Button
                                                   className="mail-modal__download-btn"
                                                   buttonType="link"
                                                   onClick={() => getPreviousAttachment(file)}
                                                >
                                                   <Icon_Download fill="none" stroke="black" />
                                                </Button>
                                                <Button
                                                   className="mail-modal__delete-btn"
                                                   buttonType="link"
                                                   onClick={() => removePreviousAttachment(file)}
                                                >
                                                   <Icon_Trash fill="#3c3c3c" stroke="none" />
                                                </Button>
                                             </div>
                                          </div>
                                       );
                                    })
                                 )}
                              </div>
                              <div className="upload-container">
                                 {selectedFiles ? (
                                    selectedFiles.map((file) => {
                                       return (
                                          <div className="mail-modal__file-attachment" key={file}>
                                             <div className="mail-modal__file-attachment__title">
                                                <p className="p1">{file?.name}</p>
                                                <span className="mail-modal__file-size">
                                                   {formatFileSize(file?.size)}
                                                </span>
                                             </div>
                                             <div className="mail-modal__btns-wrapper">
                                                <Button
                                                   className="mail-modal__download-btn"
                                                   buttonType="link"
                                                   onClick={() => downloadAttachment(file)}
                                                >
                                                   <Icon_Download fill="none" stroke="black" />
                                                </Button>
                                                <Button
                                                   className="mail-modal__delete-btn"
                                                   buttonType="link"
                                                   onClick={() => handleDeleteFile(file)}
                                                >
                                                   <Icon_Trash fill="#3c3c3c" stroke="none" />
                                                </Button>
                                             </div>
                                          </div>
                                       );
                                    })
                                 ) : (
                                    <IntlMessages id="modal.mailAttachment" />
                                 )}{" "}
                              </div>
                              <div className="footer">
                                 <Icon_Trash
                                    fill="#64748B"
                                    stroke="none"
                                    onClick={() => setSelectedFiles([])}
                                    style={{ cursor: "pointer" }}
                                 />
                                 <div className="mail-modal__btns-wrapper">
                                    <label className="upload-button">
                                       <Icon_Clip fill="#64748B" stroke="none" />
                                       <input
                                          id="file-input"
                                          type="file"
                                          onChange={handleFileChange}
                                          ref={fileInputRef}
                                       />
                                    </label>

                                    <Button
                                       htmlType="submit"
                                       buttonType="primary"
                                       disabled={isSending}
                                       onClick={confirm}
                                       className="mail-modal__submit-btn"
                                       style={{ float: "right" }}
                                    >
                                       {isSending ? (
                                          <Spin></Spin>
                                       ) : (
                                          <IntlMessages id="modal.sendMail" />
                                       )}
                                    </Button>
                                 </div>
                              </div>
                           </div>
                        </div>
                     ) : (
                        <Spin />
                     )
                  ) : (
                     <div className="connect-mail-container">
                        <IntlMessages id="mailbox.connectMailboxInfo" />{" "}
                        <a href="/messages/addMailbox" className="connect-mail-link">
                           <IntlMessages id="mailbox.here" />.
                        </a>
                     </div>
                  )
               }
            />
         )}
         <Button buttonType={buttonType} onClick={() => setIsOpen(true)} className={btnClassName}>
            {children}
         </Button>
      </>
   );
};

export default MailModal;
